import React, { createContext, useState, useContext, useCallback, useMemo, useEffect } from 'react';

export interface ZoomLevel {
  value: number;
  label: string;
}

export const ZOOM_LEVELS: ZoomLevel[] = [
  { value: 0.8, label: '80%' },
  { value: 0.85, label: '85%' },
  { value: 0.9, label: '90%' },
  { value: 0.95, label: '95%' },
  { value: 1.0, label: '100%' },
  { value: 1.05, label: '105%' },
  { value: 1.1, label: '110%' },
  { value: 1.15, label: '115%' },
  { value: 1.2, label: '120%' },
];

interface ZoomContextType {
  zoomLevel: number;
  setZoomLevel: (level: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  zoomLevels: ZoomLevel[];
  /** CSS styles to apply to the zoom container */
  zoomContainerStyle: React.CSSProperties;
}

const ZoomContext = createContext<ZoomContextType>({
  zoomLevel: 1.0,
  setZoomLevel: () => {},
  zoomIn: () => {},
  zoomOut: () => {},
  resetZoom: () => {},
  zoomLevels: ZOOM_LEVELS,
  zoomContainerStyle: {},
});

export const useZoom = () => useContext(ZoomContext);

interface ZoomProviderProps {
  children: React.ReactNode;
}

export const ZoomProvider: React.FC<ZoomProviderProps> = ({ children }) => {
  // Load zoom preference from localStorage
  const [zoomLevel, setZoomLevelState] = useState<number>(() => {
    const stored = localStorage.getItem('zoomLevel');
    if (stored) {
      const parsed = parseFloat(stored);
      if (!isNaN(parsed) && parsed >= 0.8 && parsed <= 1.2) {
        return parsed;
      }
    }
    return 1.0; // Default to 100%
  });

  // Calculate container styles based on zoom level
  // Using transform: scale() which works better with MUI popups than CSS zoom
  const zoomContainerStyle = useMemo<React.CSSProperties>(() => {
    if (zoomLevel === 1.0) {
      return {
        minHeight: '100vh',
      };
    }
    // When zoomed out, we need to scale up the container dimensions
    // so that after scaling, it fills the viewport properly
    return {
      transform: `scale(${zoomLevel})`,
      transformOrigin: 'top left',
      width: `${100 / zoomLevel}%`,
      height: `${100 / zoomLevel}vh`,
      minHeight: `${100 / zoomLevel}vh`,
      overflow: 'auto',
    };
  }, [zoomLevel]);

  // Hide body overflow when zoomed to prevent showing empty space
  useEffect(() => {
    if (zoomLevel !== 1.0) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, [zoomLevel]);

  const setZoomLevel = useCallback((level: number) => {
    // Clamp to valid range
    const clamped = Math.max(0.8, Math.min(1.2, level));
    setZoomLevelState(clamped);
    localStorage.setItem('zoomLevel', clamped.toString());
  }, []);

  const zoomIn = useCallback(() => {
    setZoomLevelState((prev) => {
      const currentIndex = ZOOM_LEVELS.findIndex((z) => z.value === prev);
      let newLevel = prev;
      if (currentIndex < ZOOM_LEVELS.length - 1) {
        newLevel = ZOOM_LEVELS[currentIndex + 1].value;
      } else {
        // If not at an exact level, find next higher
        const nextLevel = ZOOM_LEVELS.find((z) => z.value > prev);
        newLevel = nextLevel ? nextLevel.value : prev;
      }
      localStorage.setItem('zoomLevel', newLevel.toString());
      return newLevel;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevelState((prev) => {
      const currentIndex = ZOOM_LEVELS.findIndex((z) => z.value === prev);
      let newLevel = prev;
      if (currentIndex > 0) {
        newLevel = ZOOM_LEVELS[currentIndex - 1].value;
      } else {
        // If not at an exact level, find next lower
        const levels = [...ZOOM_LEVELS].reverse();
        const prevLevel = levels.find((z) => z.value < prev);
        newLevel = prevLevel ? prevLevel.value : prev;
      }
      localStorage.setItem('zoomLevel', newLevel.toString());
      return newLevel;
    });
  }, []);

  const resetZoom = useCallback(() => {
    setZoomLevelState(1.0);
    localStorage.setItem('zoomLevel', '1.0');
  }, []);

  return (
    <ZoomContext.Provider
      value={{
        zoomLevel,
        setZoomLevel,
        zoomIn,
        zoomOut,
        resetZoom,
        zoomLevels: ZOOM_LEVELS,
        zoomContainerStyle,
      }}
    >
      {children}
    </ZoomContext.Provider>
  );
};
